import type { ObstacleKind, ObstacleState, Vec3 } from './models';
import { hashCell, SeededRandom } from './rng';

export const CELL_SIZE = 32;
const KINDS: readonly ObstacleKind[] = ['barrier', 'prism', 'tetra', 'relay'];
const RADII: Record<ObstacleKind, number> = {
  barrier: 3.8,
  prism: 3.1,
  tetra: 2.8,
  relay: 2.2,
};

export function generateCellObstacles(seed: number, cellX: number, cellZ: number): ObstacleState[] {
  const random = new SeededRandom(hashCell(seed, cellX, cellZ));
  const count = random.next() < 0.22 ? 0 : random.next() < 0.68 ? 1 : 2;
  const obstacles: ObstacleState[] = [];

  for (let index = 0; index < count; index += 1) {
    const kind = KINDS[random.int(0, KINDS.length)] ?? 'barrier';
    const inset = 5;
    const localX = random.range(inset, CELL_SIZE - inset);
    const localZ = random.range(inset, CELL_SIZE - inset);
    const position = {
      x: cellX * CELL_SIZE + localX,
      y: 0,
      z: cellZ * CELL_SIZE + localZ,
    };

    // The origin is the fixed new-run clear pocket. This is a pure world rule,
    // not a dynamic player-dependent deletion, so cell contents remain stable.
    if (Math.hypot(position.x, position.z) < 14) continue;

    if (obstacles.some((other) => Math.hypot(other.position.x - position.x, other.position.z - position.z) < 8)) {
      continue;
    }

    obstacles.push({
      id: `${cellX}:${cellZ}:${index}`,
      kind,
      position,
      yaw: random.range(0, Math.PI * 2),
      radius: RADII[kind],
    });
  }

  return obstacles;
}

export function buildDemoWorld(seed: number): ObstacleState[] {
  const result: ObstacleState[] = [];
  for (let cellX = -3; cellX <= 3; cellX += 1) {
    for (let cellZ = -1; cellZ <= 4; cellZ += 1) {
      for (const obstacle of generateCellObstacles(seed, cellX, cellZ)) {
        result.push(obstacle);
      }
    }
  }
  return result;
}

export function buildWorldAround(seed: number, worldX: number, worldZ: number, radius = 4): ObstacleState[] {
  return buildWorldAroundPoints(seed, [{ x: worldX, y: 0, z: worldZ }], radius);
}

export function buildWorldAroundPoints(seed: number, centers: readonly Vec3[], radius = 4): ObstacleState[] {
  const cells = new Set<string>();
  const result: ObstacleState[] = [];
  for (const center of centers) {
    const centerX = Math.floor(center.x / CELL_SIZE);
    const centerZ = Math.floor(center.z / CELL_SIZE);
    for (let cellX = centerX - radius; cellX <= centerX + radius; cellX += 1) {
      for (let cellZ = centerZ - radius; cellZ <= centerZ + radius; cellZ += 1) {
        const key = `${cellX}:${cellZ}`;
        if (cells.has(key)) continue;
        cells.add(key);
        result.push(...generateCellObstacles(seed, cellX, cellZ));
      }
    }
  }
  return result;
}
